import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useState,
} from "react";

interface FileAttachment {
	url: string;
	mediaType: string;
}

interface PendingMessage {
	text: string;
	metadata?: Record<string, unknown>;
	files?: FileAttachment[];
}

interface ChatActionsContextType {
	pendingMessage: PendingMessage | null;
	triggerMessage: (
		text: string,
		metadata?: Record<string, unknown>,
		files?: FileAttachment[],
	) => void;
	consumeMessage: () => PendingMessage | null;
	prefillQuestion: string | null;
	setPrefillQuestion: (question: string) => void;
	consumePrefill: () => string | null;
}

const ChatActionsContext = createContext<ChatActionsContextType | undefined>(
	undefined,
);

export function ChatActionsProvider({ children }: { children: ReactNode }) {
	const [pendingMessage, setPendingMessage] = useState<PendingMessage | null>(
		null,
	);

	const triggerMessage = useCallback(
		(
			text: string,
			metadata?: Record<string, unknown>,
			files?: FileAttachment[],
		) => {
			setPendingMessage({ text, metadata, files });
		},
		[],
	);

	const consumeMessage = useCallback(() => {
		const message = pendingMessage;
		setPendingMessage(null);
		return message;
	}, [pendingMessage]);

	const [prefillQuestion, setPrefillQuestion] = useState<string | null>(null);

	const consumePrefill = useCallback(() => {
		const question = prefillQuestion;
		setPrefillQuestion(null);
		return question;
	}, [prefillQuestion]);

	const handleSetPrefillQuestion = useCallback((question: string) => {
		setPrefillQuestion(question);
	}, []);

	return (
		<ChatActionsContext.Provider
			value={{
				pendingMessage,
				triggerMessage,
				consumeMessage,
				prefillQuestion,
				setPrefillQuestion: handleSetPrefillQuestion,
				consumePrefill,
			}}
		>
			{children}
		</ChatActionsContext.Provider>
	);
}

export function useChatActions() {
	const context = useContext(ChatActionsContext);
	if (context === undefined) {
		throw new Error("useChatActions must be used within a ChatActionsProvider");
	}
	return context;
}
